import React from 'react';
import { SideSheet, SideSheetProps, Box } from 'pounceV2';
import ErrorBoundaryV3 from 'Components/ErrorBoundary/ErrorBoundaryV3';
import { GenericAISummaryContextProviderV3 } from 'Components/AIExplainLogEvents/GenericAISummaryContextV3';
import { AIInferenceStreamUsage, AISummarizeKind } from 'Generated/schema';
import { getPageNameForKind } from 'Components/AIExplainLogEvents/utils/aiKindUtils';
import AISummarySidePanelContentV3 from 'Components/sidesheets/AISummarySidePanel/AISummarySidePanelContent/AISummarySidePanelContentV3';
import AISummaryConversationContentV3 from 'Components/sidesheets/AISummarySidePanel/AISummarySidePanelContent/AISummaryConversationContentV3';
import { AISummaryStreamParams } from 'Components/AIExplainLogEvents/useFetchGenericAiSummaryV3';
import { ConversationalAIParams } from 'Pages/AI/types';
import useUrlParams from 'Hooks/useUrlParams';
import useFeatureToggle from 'Hooks/useFeatureToggle';
import ResizeHandleV3 from 'Components/ResizeHandle/ResizeHandleV3';
import storage from 'Helpers/storage';
import { AI_SIDEPANEL_WIDTH_STORAGE_KEY } from 'Components/Navigation/constants';
import { useGetAIInferenceStreamMetadata } from 'Components/AIExplainLogEvents/graphql/getAiInferenceStreamMetadata.generated';
import { isNewStreamId } from 'Pages/AI/constants';
import { useTheme } from '@mui/material';
import { CONVERSATIONAL_KINDS } from 'Pages/AI/graphql/aiConversations';

export interface AiSummarySidesheetProps extends SideSheetProps {
  kind: AISummarizeKind;
  alias: string;
  events?: string[];
  streamCompletedActions?: (aiSummary: string) => React.ReactNode;
  onStreamComplete?: (summary: string, userPrompt: string, usage?: AIInferenceStreamUsage) => void;
  /** When true, skip existing conversation lookup and start a fresh conversation */
  forceNewConversation?: boolean;
}

const AiSummarySidesheetV3: React.FC<AiSummarySidesheetProps> = ({
  kind,
  alias,
  events,
  streamCompletedActions,
  onStreamComplete,
  forceNewConversation = false,
  onClose,
  ...rest
}) => {
  const isConversationalKind = CONVERSATIONAL_KINDS.has(kind);
  const {
    palette: { tokens },
  } = useTheme();
  const {
    updateUrlParams,
    urlParams: { aiStream, parentStreamId },
  } = useUrlParams<AISummaryStreamParams & ConversationalAIParams>();
  const enablePrivacyControls = useFeatureToggle('ENABLE_AI_PRIVACY_CONTROLS');

  // Read privacy from server for existing streams
  const streamIdForMetadata = isNewStreamId(aiStream) ? parentStreamId : aiStream;
  const { data: streamMetadata } = useGetAIInferenceStreamMetadata({
    variables: { id: streamIdForMetadata || '' },
    skip: !streamIdForMetadata,
  });
  const defaultPrivate = enablePrivacyControls && kind !== AISummarizeKind.Alert;
  // When privacy controls are disabled, always use false (shared)
  const isPrivate = enablePrivacyControls
    ? (streamMetadata?.aiInferenceStream?.private ?? defaultPrivate)
    : false;

  const metadata = React.useMemo(() => {
    return {
      kind,
      page: getPageNameForKind(kind),
    };
  }, [kind]);

  // Get default width: 60% of viewport or minimum 980px
  const getDefaultWidth = React.useCallback(() => {
    if (typeof window === 'undefined') {
      return 980;
    }
    return Math.max(window.innerWidth * 0.6, 980);
  }, []);

  // Initialize panel width from localStorage or default
  const [panelWidth, setPanelWidth] = React.useState<number>(() => {
    return storage.local.read<number>(AI_SIDEPANEL_WIDTH_STORAGE_KEY) ?? getDefaultWidth();
  });

  const handleResize = React.useCallback((deltaX: number) => {
    setPanelWidth(prevWidth => {
      // Calculate new width (deltaX is positive when dragging left)
      const newWidth = prevWidth + deltaX;
      // Constrain between 400px (minimum) and viewport width minus left navigation (maximum)
      const minWidth = 400;
      const maxWidth = window.innerWidth * 0.9;
      const constrainedWidth = Math.max(minWidth, Math.min(newWidth, maxWidth));

      // Persist to localStorage
      storage.local.write(AI_SIDEPANEL_WIDTH_STORAGE_KEY, constrainedWidth);

      return constrainedWidth;
    });
  }, []);

  const handleClose = React.useCallback(() => {
    onClose?.();
    updateUrlParams({ aiStream: undefined, conversationId: undefined });
  }, [onClose, updateUrlParams]);

  return (
    <SideSheet
      aria-label="AI Summary"
      onClose={handleClose}
      hideDismissIcon={true}
      {...rest}
      containerProps={{
        width: `${panelWidth}px`,
        maxWidth: '99vw',
        p: 0,
        backgroundColor: tokens.background.neutral.canvas.base,
      }}
    >
      <ResizeHandleV3 onResize={handleResize} />
      {isConversationalKind ? (
        <ErrorBoundaryV3>
          <AISummaryConversationContentV3
            kind={kind}
            alias={alias}
            events={events}
            onClose={handleClose}
            forceNewConversation={forceNewConversation}
          />
        </ErrorBoundaryV3>
      ) : (
        <Box>
          <ErrorBoundaryV3>
            <GenericAISummaryContextProviderV3
              kind={kind}
              alias={alias}
              metadata={metadata}
              syncStreamIdWithUrl
              onStreamComplete={onStreamComplete}
              private={isPrivate}
            >
              <AISummarySidePanelContentV3
                kind={kind}
                alias={alias}
                onClose={handleClose}
                streamCompletedActions={streamCompletedActions}
                isPrivate={isPrivate}
              />
            </GenericAISummaryContextProviderV3>
          </ErrorBoundaryV3>
        </Box>
      )}
    </SideSheet>
  );
};

export default AiSummarySidesheetV3;
